import { useEffect } from 'react';

const SEOHead = ({ config }) => {
  // Update meta tags dynamically
  useEffect(() => {
    if (!config) return;

    // Update title
    if (config.seo_title) {
      document.title = config.seo_title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (selector, content, attribute = 'content') => {
      if (!content) return;

      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const attrName = selector.includes('property=') ? 'property' : 'name';
        const attrValue = selector.match(/['"]([^'"]+)['"]/)[1];
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Update or create link tag
    const updateLinkTag = (rel, href) => {
      if (!href) return;

      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic SEO meta tags
    updateMetaTag('meta[name="description"]', config.seo_description);
    updateMetaTag('meta[name="keywords"]', config.seo_keywords);
    updateMetaTag('meta[name="author"]', config.seo_author);
    updateMetaTag('meta[name="robots"]', config.seo_robots);

    // Language
    if (config.seo_language) {
      document.documentElement.setAttribute('lang', config.seo_language);
    }

    // Canonical URL
    updateLinkTag('canonical', config.seo_canonical_url);

    // Open Graph tags
    updateMetaTag('meta[property="og:type"]', 'website');
    updateMetaTag('meta[property="og:title"]', config.seo_title);
    updateMetaTag('meta[property="og:description"]', config.seo_description);
    updateMetaTag('meta[property="og:url"]', config.seo_canonical_url);
    updateMetaTag('meta[property="og:image"]', config.seo_og_image);

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', config.seo_title);
    updateMetaTag('meta[name="twitter:description"]', config.seo_description);
    updateMetaTag('meta[name="twitter:image"]', config.seo_og_image);

    // Theme color
    updateMetaTag('meta[name="theme-color"]', '#14b8a6');

  }, [config]);

  // Google Analytics
  useEffect(() => {
    if (!config.analytics_google_id) return;

    const scriptId = 'ga-script';
    if (document.getElementById(scriptId)) return;

    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.id = scriptId;
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${config.analytics_google_id}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${config.analytics_google_id}');
    `;
    document.head.appendChild(script2);

  }, [config.analytics_google_id]);

  // Google Tag Manager
  useEffect(() => {
    if (!config.analytics_gtm_id) return;

    const scriptId = 'gtm-script';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${config.analytics_gtm_id}');
    `;
    document.head.appendChild(script);

  }, [config.analytics_gtm_id]);

  // Meta Pixel (Facebook)
  useEffect(() => {
    if (!config.analytics_meta_pixel) return;

    const scriptId = 'fb-pixel';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${config.analytics_meta_pixel}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${config.analytics_meta_pixel}&ev=PageView&noscript=1" />`;
    document.body.appendChild(noscript);

  }, [config.analytics_meta_pixel]);

  // Hotjar
  useEffect(() => {
    if (!config.analytics_hotjar_id) return;

    const scriptId = 'hotjar-script';
    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.innerHTML = `
      (function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:${config.analytics_hotjar_id},hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    document.head.appendChild(script);

  }, [config.analytics_hotjar_id]);

  return null; // This component doesn't render anything
};

export default SEOHead;
